import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { ResolveIeltsMockTest, ResolvePteMockTest } from '../../Client/request';
import { Base_URL } from '../../Client/apiURL';
import { getSharedRouteAction, parseTestKey, storeLaunchGrant } from '../../utils/mockTestLinks';
import MockTestFull from '../../components/MockTest/MockTestFull';
import MockTestSectional from '../../components/MockTest/MockTestSectional';
import IELTSFullTestPage from '../IELTSFullTestPage';
import ListeningTestPage from '../ListeningTestPage';
import ReadingTestPage from '../ReadingTestPage';
import WritingTestPage from '../WritingTestPage';
import SpeakingTestPage from '../SpeakingTestPage';

const RunnerByCategory = {
  Listening: ListeningTestPage,
  Reading: ReadingTestPage,
  Writing: WritingTestPage,
  Speaking: SpeakingTestPage,
};

function responseError(data, fallback) {
  const error = new Error(data?.message || fallback);
  error.status = data?.responseCode;
  error.errorCode = data?.errorCode;
  return error;
}

export default function SharedMockTestRoute({ exam }) {
  const { scope, testKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = useMemo(() => parseTestKey(testKey), [testKey]);
  const [resolved, setResolved] = useState(location.state?.resolvedTest || null);
  const [loading, setLoading] = useState(Boolean(parsed) && !location.state?.resolvedTest);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(parsed ? null : 'This mock-test link is invalid.');
  const autoLaunchStarted = useRef(false);
  const resolvedMatchesRoute = Boolean(
    resolved
    && parsed
    && resolved.exam === exam
    && resolved.scope === scope
    && Number(resolved.id) === Number(parsed.id)
  );

  useEffect(() => {
    autoLaunchStarted.current = false;
  }, [exam, parsed?.id, scope]);

  const loadMetadata = useCallback(async () => {
    if (!parsed) return;
    setLoading(true);
    setError(null);
    try {
      const data = exam === 'pte'
        ? await ResolvePteMockTest(scope, parsed.id)
        : await ResolveIeltsMockTest(scope, parsed.id);
      if (data?.responseCode !== 200 || !data?.response) throw responseError(data, 'Unable to load this mock test.');
      setResolved(data.response);
      if (data.response.canonicalPath && data.response.canonicalPath !== location.pathname) {
        navigate(data.response.canonicalPath, { replace: true, state: location.state });
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Unable to load this mock test.');
    } finally {
      setLoading(false);
    }
  }, [exam, location.pathname, location.state, navigate, parsed, scope]);

  useEffect(() => {
    if (!location.state?.launched) loadMetadata();
  }, [loadMetadata, location.state?.launched]);

  const launch = useCallback(async () => {
    if (!resolved?.access?.canStart && !resolved?.access?.canResume) return;
    setLaunching(true);
    setError(null);
    try {
      const launchData = exam === 'pte'
        ? await ResolvePteMockTest(scope, parsed.id, true)
        : await ResolveIeltsMockTest(scope, parsed.id, true);
      if (launchData?.responseCode !== 200 || !launchData?.response) throw responseError(launchData, 'Unable to start this mock test.');
      const context = launchData.response;
      storeLaunchGrant({ exam, scope, id: context.id }, context.launchGrant);

      if (exam === 'pte') {
        navigate(location.pathname, {
          replace: true,
          state: { launched: true, resolvedTest: context, Exam: context.runnerData, launchGrant: context.launchGrant },
        });
        return;
      }

      if (scope === 'full') {
        if (context.resumeAttemptId && !location.state?.forceRestart) {
          const resumeResponse = await fetch(
            `${Base_URL}/app/users/ielts-tests/full-attempts/${context.resumeAttemptId}/resume-context`,
            { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
          );
          const resumeData = await resumeResponse.json();
          if (!resumeResponse.ok || resumeData?.responseCode !== 200) throw responseError(resumeData, 'Unable to resume this IELTS test.');
          navigate(location.pathname, {
            replace: true,
            state: {
              launched: true,
              resolvedTest: context,
              groupedTests: context.runnerData.groupedTests,
              groupId: context.id,
              mode: location.state?.requestedMode || 'practice',
              continuePrevious: true,
              resumeFullTestAttemptId: context.resumeAttemptId,
              prefetchedResumeContext: resumeData.response,
              launchGrant: context.launchGrant,
            },
          });
        } else {
          navigate(location.pathname, {
            replace: true,
            state: {
              launched: true,
              resolvedTest: context,
              groupedTests: context.runnerData.groupedTests,
              groupId: context.id,
              mode: location.state?.requestedMode || 'practice',
              continuePrevious: false,
              launchGrant: context.launchGrant,
            },
          });
        }
        return;
      }

      const detailResponse = await fetch(`${Base_URL}/app/users/ielts-tests/getTestById/${context.id}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Test-Launch-Token': context.launchGrant },
      });
      const detailData = await detailResponse.json();
      if (!detailResponse.ok || detailData?.responseCode !== 200 || !detailData?.response) {
        throw responseError(detailData, 'Unable to prepare this IELTS test.');
      }
      navigate(location.pathname, {
        replace: true,
        state: {
          launched: true,
          resolvedTest: context,
          testData: detailData.response,
          launchGrant: detailData.response.launchGrant || context.launchGrant,
          mode: location.state?.requestedMode === 'resume' ? undefined : location.state?.requestedMode,
          resumeDraft: Boolean(location.state?.resumeDraft || location.state?.requestedMode === 'resume'),
          currentUserId: JSON.parse(localStorage.getItem('userData') || '{}')?.UserId,
        },
      });
    } catch (launchError) {
      setError(launchError?.response?.data?.message || launchError.message || 'Unable to start this mock test.');
    } finally {
      setLaunching(false);
    }
  }, [exam, location.pathname, location.state, navigate, parsed, resolved, scope]);

  useEffect(() => {
    if (
      location.state?.launched
      || error
      || !resolvedMatchesRoute
      || getSharedRouteAction(resolved) !== 'launch'
      || autoLaunchStarted.current
    ) return;

    autoLaunchStarted.current = true;
    launch();
  }, [error, launch, location.state?.launched, resolved, resolvedMatchesRoute]);

  if (location.state?.launched) {
    if (exam === 'pte') {
      return scope === 'full' ? <MockTestFull /> : <MockTestSectional />;
    }
    if (scope === 'full') return <IELTSFullTestPage />;
    const category = location.state?.testData?.category_name;
    const Runner = RunnerByCategory[category];
    return Runner ? <Runner /> : <Alert severity="error">This IELTS section is unavailable.</Alert>;
  }

  const routeAction = getSharedRouteAction(resolvedMatchesRoute ? resolved : null);
  if (loading || launching || (parsed && !resolvedMatchesRoute && !error) || routeAction === 'launch') {
    return <Box minHeight="60vh" display="grid" sx={{ placeItems: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 5 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" fontWeight={800}>{resolved?.name || 'Mock test'}</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {[resolved?.variant, resolved?.category, resolved?.scope].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
          {resolved?.access?.requiresSubscription && (
            <Alert severity="info" icon={<LockOutlinedIcon />}>A subscription is required to start this mock test.</Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            {resolved && routeAction === 'locked' && (
              <Button variant="contained" onClick={() => navigate('/sidebar', { state: { section: 'Plan Info', from: location.pathname } })}>
                Upgrade
              </Button>
            )}
            <Button variant="text" onClick={() => navigate(exam === 'pte' ? '/MockTest' : '/ielts-dashboard')}>Back to tests</Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
